import { Divider, Stack } from '@mui/material'
import Drawer from '@mui/material/Drawer'
import {
  cancelSubscription,
  changeSubscriptionPlan,
  deleteBillingProfile,
  syncSubscription,
} from 'api/subscription'
import { removeFamilyMember } from 'api/subscriptionGroups'
import { useAuthContext } from 'components/context/AuthProvider'
import { TeamsContext } from 'features/Timeline/hooks/useTeams'
import DrawerContainer from 'components/drawer/common/DrawerContainer'
import DrawerHeader from 'components/drawer/common/DrawerHeader'
import ConfirmModal from 'components/modals/ConfirmModal'
import NotificationSys from 'components/NotificationSystem'
import { defaultDrawerSX } from 'const'
import { useContext, useState } from 'react'
import { IFamilyMemberDto, SubscriptionPlanType, SubscriptionStatus } from 'types'
import { formatDate } from 'utils'
import { usePayNow } from '../hooks/usePayNow'
import {
  useBillingProfile,
  useCurrentSubscription,
  useInvalidateBillingProfile,
  useInvalidateSubscriptionGroup,
  useMySubscriptionGroup,
  usePaymentHistory,
} from '../hooks/useSubscription'
import { AddCardModal } from './AddCardModal'
import { PaymentHistoryTable } from './PaymentHistoryTable'
import { PaymentMethodSection } from './PaymentMethodSection'
import { SubscriptionPlanSection } from './SubscriptionPlanSection'
import { TraineeSubscriptionSection } from './TraineeSubscriptionSection'
import { SubscriptionWarningBanner } from './SubscriptionWarningBanner'
import { isFamilyPlan, isPartnerFunded } from '../utils'

export function MySubscriptionDrawer({
  isOpen,
  handleClose,
}: {
  isOpen: boolean
  handleClose: () => void
}) {
  const subscription = useCurrentSubscription()
  const { billingProfile, isLoading } = useBillingProfile()
  const invalidateBillingProfile = useInvalidateBillingProfile()
  const invalidateSubscriptionGroup = useInvalidateSubscriptionGroup()
  const { refreshUser, user, isIndividualTrainee } = useAuthContext()
  const teamsContext = useContext(TeamsContext)
  const onRefetchTeamMembers = teamsContext?.onRefetchTeamMembers ?? (async () => {})
  const familyContext = user?.familyContext ?? null
  const isFamilyOwner = familyContext?.role === 'owner' && isFamilyPlan(subscription?.plan)
  const isFamilyMember = familyContext?.role === 'member'
  // Partner-funded owners have no billing of their own — the partner pays.
  const hasOwnBilling = !isFamilyMember && !isPartnerFunded(subscription)

  const { group } = useMySubscriptionGroup(isFamilyOwner)
  const familyMembers = group?.members ?? []

  const [addCardModalOpen, setAddCardModalOpen] = useState(false)
  const [removeCardConfirmOpen, setRemoveCardConfirmOpen] = useState(false)
  const [removeCardLoading, setRemoveCardLoading] = useState(false)
  const [changePlanLoading, setChangePlanLoading] = useState(false)
  const [memberToRemove, setMemberToRemove] = useState<IFamilyMemberDto | null>(null)
  const [removeMemberLoading, setRemoveMemberLoading] = useState(false)
  const [unsubscribeConfirmOpen, setUnsubscribeConfirmOpen] = useState(false)
  const [unsubscribeLoading, setUnsubscribeLoading] = useState(false)

  const { paymentHistory } = usePaymentHistory()

  const { handlePayNow, payNowLoading } = usePayNow({
    billingProfile,
    subscription,
    onNeedCard: () => setAddCardModalOpen(true),
  })

  const handleAddCardSuccess = () => {
    setAddCardModalOpen(false)
    invalidateBillingProfile()
  }

  const handleChangePlan = async (plan: SubscriptionPlanType) => {
    if (!subscription || plan === subscription.plan) return

    try {
      setChangePlanLoading(true)
      const response = await changeSubscriptionPlan(subscription.id, plan)
      if (response.data.stripeErrorMessage) {
        NotificationSys.showWarning(response.data.stripeErrorMessage)
        return
      }
      if (!response.data.subscriptionGroupId && response.data.userSubscriptionId) {
        await syncSubscription(response.data.userSubscriptionId)
      }
      await refreshUser()
      NotificationSys.showSuccess('Subscription plan updated.')
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to change plan.'
      NotificationSys.showWarning(message)
    } finally {
      setChangePlanLoading(false)
    }
  }

  // A care-team add by a Family owner reserves a Family seat on the backend — refresh group + seat counts.
  const handleFamilyMemberAdded = async () => {
    invalidateSubscriptionGroup()
    await refreshUser()
  }

  const handleRemoveMember = async () => {
    if (!memberToRemove) return
    try {
      setRemoveMemberLoading(true)
      await removeFamilyMember(memberToRemove.id)
      invalidateSubscriptionGroup()
      await refreshUser()
      await onRefetchTeamMembers()
      NotificationSys.showSuccess(
        `${memberToRemove.email} removed from your Family Subscription and care team.`,
      )
      setMemberToRemove(null)
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to remove member.'
      NotificationSys.showWarning(message)
    } finally {
      setRemoveMemberLoading(false)
    }
  }

  // Auto-renewal off; the backend keeps the plan active until currentPeriodEnd.
  const handleUnsubscribe = async () => {
    try {
      setUnsubscribeLoading(true)
      await cancelSubscription()
      setUnsubscribeConfirmOpen(false)
      await refreshUser()
      NotificationSys.showSuccess('Auto-renewal has been turned off.')
    } catch {
      // The global error handler has already shown the backend message.
    } finally {
      setUnsubscribeLoading(false)
    }
  }

  const handleRemoveCard = async () => {
    if (!billingProfile) return

    try {
      setRemoveCardLoading(true)
      await deleteBillingProfile(billingProfile.id)
      setRemoveCardConfirmOpen(false)
      invalidateBillingProfile()
      NotificationSys.showSuccess('Card removed successfully.')
    } catch {
      NotificationSys.showWarning('Failed to remove card.')
    } finally {
      setRemoveCardLoading(false)
    }
  }

  const showWarning =
    subscription &&
    subscription.status !== SubscriptionStatus.Active &&
    subscription.status !== SubscriptionStatus.Trialing

  return (
    <>
      <Drawer anchor="right" open={isOpen} onClose={handleClose} sx={defaultDrawerSX}>
        {isOpen && (
          <DrawerContainer
            isOpen={isOpen}
            hasData={!!subscription}
            loading={isLoading}
            handleClose={handleClose}
          >
            <DrawerHeader handleClose={handleClose}>My Subscription</DrawerHeader>

            {subscription && (
              <Stack spacing={3}>
                {/* Members don't manage billing — the owner does. Hide pay-now warning, card, history. */}
                {showWarning && hasOwnBilling && (
                  <SubscriptionWarningBanner
                    status={subscription.status}
                    onPayNow={handlePayNow}
                    loading={payNowLoading}
                    disabled={!billingProfile}
                  />
                )}

                {isIndividualTrainee ? (
                  <TraineeSubscriptionSection subscription={subscription} />
                ) : (
                  <SubscriptionPlanSection
                    subscription={subscription}
                    familyContext={familyContext}
                    familyMembers={familyMembers}
                    onChangePlan={handleChangePlan}
                    changePlanLoading={changePlanLoading}
                    onRemoveFamilyMember={setMemberToRemove}
                    removingMemberId={removeMemberLoading ? memberToRemove?.id : null}
                    onFamilyMemberAdded={handleFamilyMemberAdded}
                    onUnsubscribe={() => setUnsubscribeConfirmOpen(true)}
                  />
                )}

                {hasOwnBilling && (
                  <>
                    <Divider />

                    <PaymentMethodSection
                      billingProfile={billingProfile}
                      onAddCard={() => setAddCardModalOpen(true)}
                      onRemoveCard={() => setRemoveCardConfirmOpen(true)}
                      disabled={isLoading}
                    />

                    <Divider />

                    <PaymentHistoryTable history={paymentHistory} />
                  </>
                )}
              </Stack>
            )}
          </DrawerContainer>
        )}
      </Drawer>

      <AddCardModal
        isOpen={addCardModalOpen}
        handleClose={() => setAddCardModalOpen(false)}
        onSuccess={handleAddCardSuccess}
      />

      {removeCardConfirmOpen && (
        <ConfirmModal
          title="Remove Card"
          body="Are you sure you want to remove this payment method? You will need to add a new card to continue your subscription."
          loading={removeCardLoading}
          handleConfirm={handleRemoveCard}
          handleClose={() => setRemoveCardConfirmOpen(false)}
        />
      )}

      {unsubscribeConfirmOpen && subscription && (
        <ConfirmModal
          title="Unsubscribe"
          body={`Are you sure you want to unsubscribe? Your subscription will remain active until ${formatDate(
            subscription.currentPeriodEnd,
            'MM.DD.YY',
          )}. You will not be charged again after cancellation, and access to the system will end after the current billing period.`}
          loading={unsubscribeLoading}
          handleConfirm={handleUnsubscribe}
          handleClose={() => (unsubscribeLoading ? null : setUnsubscribeConfirmOpen(false))}
        />
      )}

      {memberToRemove && (
        <ConfirmModal
          title="Remove Family Member"
          body={
            <>
              Are you sure you want to remove <strong>{memberToRemove.email}</strong> from your
              Family Subscription and Care Team? They will{' '}
              <strong>immediately lose access to the system</strong> unless they purchase their own
              subscription.
            </>
          }
          loading={removeMemberLoading}
          handleConfirm={handleRemoveMember}
          handleClose={() => (removeMemberLoading ? null : setMemberToRemove(null))}
        />
      )}
    </>
  )
}
